import { audio } from '../index';

/** Hover sounds fire at most this often (spec §6). */
export const HOVER_MIN_MS = 60;

/**
 * UI sounds for every button and checkbox, by event delegation, so each component needn't wire
 * its own: hover, click, toggle, and back (`data-sound="back"`, e.g. a close button). A button
 * with its own sound (shop actions) opts out with `data-sound="off"`; disabled ones stay quiet.
 */
export function installUiSounds(
  root: Document = document,
  now: () => number = () => performance.now(),
): () => void {
  let lastHover = -Infinity;
  let hovered: Element | null = null;

  const button = (t: EventTarget | null): HTMLElement | null => {
    const el = t instanceof Element ? t.closest('button, [role="button"]') : null;
    if (!(el instanceof HTMLElement) || el.dataset.sound === 'off') return null;
    if (el.matches(':disabled') || el.getAttribute('aria-disabled') === 'true') return null;
    return el;
  };

  const onOver = (e: Event) => {
    const el = button(e.target);
    if (el === hovered) return;
    hovered = el;
    if (!el || now() - lastHover < HOVER_MIN_MS) return;
    lastHover = now();
    audio.play('ui.hover');
  };
  const onClick = (e: Event) => {
    const el = button(e.target);
    if (el) audio.play(el.dataset.sound === 'back' ? 'ui.back' : 'ui.click');
  };
  const onChange = (e: Event) => {
    const t = e.target;
    if (t instanceof HTMLInputElement && t.type === 'checkbox' && t.dataset.sound !== 'off') {
      audio.play('ui.toggle');
    }
  };

  root.addEventListener('pointerover', onOver);
  root.addEventListener('click', onClick);
  root.addEventListener('change', onChange);
  return () => {
    root.removeEventListener('pointerover', onOver);
    root.removeEventListener('click', onClick);
    root.removeEventListener('change', onChange);
  };
}
